import React, { useState } from "react"
import { useMutation } from "urql"

import { AnimatedModal } from "../../../components/shared/Modal"
import { useToast } from "../../../contexts/ToastContext"

import { RESYNC_CALENDAR_MANAGED_EVENTS_MUTATION } from "./graphql"
import { providerLabel } from "./selectors"
import { CalendarConnectionSummary, ResyncCalendarManagedEventsPayload } from "./types"

interface Props {
  connection: CalendarConnectionSummary | null
  onClose: () => void
  onResynced: (summary: string) => void
}

// Confirm + enqueue managed-event resync via resyncCalendarManagedEvents.
// Matches DisconnectConfirmModal (AnimatedModal + primary action).
const ResyncConfirmModal = ({ connection, onClose, onResynced }: Props) => {
  const [submitting, setSubmitting] = useState(false)
  const [, resyncManagedEvents] = useMutation<{ resyncCalendarManagedEvents: ResyncCalendarManagedEventsPayload }>(
    RESYNC_CALENDAR_MANAGED_EVENTS_MUTATION
  )
  const { showToast } = useToast()

  if (!connection) return null

  const resync = () => {
    setSubmitting(true)
    resyncManagedEvents({ connectionPublicId: connection.publicId }).then((result) => {
      setSubmitting(false)
      const payload = result.data?.resyncCalendarManagedEvents
      if (payload?.result === "success") {
        const enqueued = payload.enqueued || 0
        const skipped = payload.skipped || 0
        const summary =
          enqueued === 0
            ? `Resync complete — nothing to update (${skipped} already synced)`
            : `Resync started — ${enqueued} booking${enqueued === 1 ? "" : "s"} queued`
        showToast(summary)
        onResynced(summary)
      } else {
        showToast({ content: payload?.errors?.[0] || "Could not resync bookings", type: "error" })
      }
    })
  }

  return (
    <AnimatedModal
      header="Resync bookings to Google"
      visible={Boolean(connection)}
      hideModal={onClose}
      showFooter={true}
      actionButtonCopy={submitting ? "Resyncing…" : "Yes, resync"}
      actionButtonType="primary"
      saveDisabled={submitting}
      onSave={resync}>
      <p>
        Resync upcoming Heal.me bookings to {providerLabel(connection.provider)}
        {connection.displayIdentity ? ` (${connection.displayIdentity})` : ""}? This restores missing or trashed
        Heal.me events on your write calendar. Personal Google events are not removed.
      </p>
    </AnimatedModal>
  )
}

export default ResyncConfirmModal
